// Layer 3 (Transformations): the Control room landing (polish.P15 #702,
// locked comp `landing-control-room.html` A, finished with C's case code on
// the lever chip and B's context line under each action). Four strip-as-tabs
// cells (last shift · circuit read · loops and variables · since last shift)
// beside the onward card that opens Advisory, a status band, and one panel
// row per view. Every figure reads the readings and series at the cursor, the
// role seed, the Advisory cases and alarms, the escalation ladders and the
// shared landing derivations; every count here is derived (adjudication 12).
// No money renders on this landing (adjudication 13).

import type { Alarm, Case } from '@/versions/v1/schema/case';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import type { Reading } from '@/versions/v1/schema/reading';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { CONTROL_LOOPS } from '@/versions/v1/shared/bunker-hill-seed';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { SERIES_BY_READING_ID } from '@/versions/v1/shared/bunker-hill-series';
import { recoveryPayableLevers } from '@/versions/v1/shared/NsrEngine';
import { ladderRead, formatLadderLine, respondByOverdueClock } from '@/versions/v1/shared/ladder.transform';
import { roleShortLabel } from '@/versions/v1/shared/org';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { formatPtDelta, recoveryPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import {
  rosterPatternTooltip,
  rosterPeriods,
  sampleCountRead,
  windowContains,
  windowSinceLabel,
  type RosterPeriod,
  type RosterPeriodId,
} from '@/versions/v1/shared/roster-period.derive';
import { readingsAt, seriesAt, valueAt } from '@/versions/v1/shared/series.derive';
import { diffMinutes, formatCaseClock, type ShiftWindow } from '@/versions/v1/shared/clock.derive';
import {
  alarmsWithSession,
  casesWithSession,
  dcsAlarmsForPanel,
  severityChip,
} from '@/versions/v1/modules/advisory/Scene.transform';
import type { LandingStripCell } from './GmLeadBand.transform';
import {
  isOpenCase,
  loopsManualPastHours,
  performanceForRole,
  type PerformanceInput,
} from './Performance.transform';
import { croKpvTable } from './RoleLanding.transform';
import { croStageRead, type LeverChip } from './StageLadder.transform';
import { recoveryForecastRead } from './RecoveryTrend.transform';
import { shiftBriefEventCount, shiftBriefRead } from './ShiftBrief.transform';
import { lastShiftTonnes, znPhSetpointAt } from './OpsSupervisorLanding.transform';

export type CroTabId = 'shift' | 'circuit' | 'loops' | 'since';

const ROLE = 'cro';

/** The closed honest state for a figure the seed cannot give (DESIGN.md § Decided). */
const UNMEASURED = 'unmeasured';

const hm = (stamp: string): string => stamp.split('T')[1] ?? stamp;
/** A no-break space keeps a count on its noun and a figure on its unit when a
 *  narrow cell wraps (`1 DCS alarm`, `8.2 h`). */
const NBSP = '\u00a0';
const plural = (n: number, one: string, many = `${one}s`) => `${n}${NBSP}${(n === 1 ? one : many).replace(/ /g, NBSP)}`;
const bindUnit = (text: string) => text.replace(/(\d) (?=[a-zµ%°])/g, `$1${NBSP}`);
/** An escalation or respond-by clock keeps its label on its figure (`in 20m`,
 *  `by 10m`, `1h 05m`), so a narrow column never orphans the figure (item 7). */
const bindClock = (text: string) =>
  text
    .replace(/\b(in|by)\s+(\d)/g, (_match, lead: string, digit: string) => `${lead}${NBSP}${digit}`)
    .replace(/(\d+h)\s+(\d+m)/g, (_match, hours: string, minutes: string) => `${hours}${NBSP}${minutes}`);
const fmtT = (n: number) => Math.round(n).toLocaleString('en-US');
const signed = (n: number, unit: string) => `${n >= 0 ? '+' : '−'}${fmtT(Math.abs(n))} ${unit}`;

/** A derivation that cannot answer degrades to its fallback: no render path
 *  throws at any cursor (the P13 lesson). */
function safe<T>(read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    return fallback;
  }
}

function readingAt(id: string, state: SessionState): Reading | undefined {
  return readingsAt(state).find((row) => row.id === id) ?? readingByIdLookup(id);
}

/** A reading's mean over a window, from its own series; the value at the
 *  cursor where the reading carries no series or the window holds no point. */
function windowValue(readingId: string, state: SessionState, window: ShiftWindow | null): number | undefined {
  const atCursor = valueAt(readingId, state) ?? readingByIdLookup(readingId)?.value;
  if (!window) return atCursor;
  const series = SERIES_BY_READING_ID[readingId];
  if (!series) return atCursor;
  const points = safe(() => seriesAt(series.id, state).points, []).filter(
    (point): point is { at: string; value: number } =>
      point.value !== null && diffMinutes(point.at, window.start) >= 0 && diffMinutes(point.at, state.cursor) <= 0,
  );
  if (points.length === 0) return atCursor;
  return points.reduce((sum, point) => sum + point.value, 0) / points.length;
}

// ---- The period ------------------------------------------------------------------------

export type CroPeriods = {
  periods: readonly RosterPeriod[];
  tooltip: string;
};

/** The roster periods the control switches between (`Last shift · 7 days` at
 *  the demo cursor, where since-last equals last shift). */
export function croPeriods(state: SessionState): CroPeriods {
  const periods = rosterPeriods(ROLE, state.cursor);
  return { periods, tooltip: rosterPatternTooltip(ROLE, periods) };
}

/** The period in force: the chosen one, else last shift (the since-last
 *  default folds into it when the two windows start together). */
export function croActivePeriod(state: SessionState, chosen: RosterPeriodId): RosterPeriod | null {
  const { periods } = croPeriods(state);
  return (
    periods.find((row) => row.id === chosen) ??
    periods.find((row) => row.id === 'last-shift') ??
    periods[0] ??
    null
  );
}

const isSevenDays = (period: RosterPeriod | null) => period?.id === 'seven-days';

/** The header row: the landing's subtitle and the period's assay count. */
export function croHeader(state: SessionState, period: RosterPeriod | null): { subtitle: string; sampleLine?: string } {
  const basis = readingByIdLookup('rd-kpi-milled-day')?.design !== undefined ? 'design' : 'budget';
  return {
    subtitle: `Shift performance vs ${basis}`,
    sampleLine: period ? safe(() => sampleCountRead(state, period.window, ROLE, period.id).label, undefined) : undefined,
  };
}

// ---- Cell 1: tonnes over the last completed shift, or the 7-day window -----------------

export type CroTonnes = {
  name: string;
  actual: number;
  budget: number;
  design?: number;
  window: string;
};

/** Tonnes milled in the period's completed shifts (a running shift is never an
 *  actual): the last shift through P13's `lastShiftTonnes()`; over 7 days the
 *  day- and night-shift series summed over every shift that ended by the
 *  current shift's start, against 720 t budget and the shift design each. */
export function croTonnes(state: SessionState, period: RosterPeriod | null): CroTonnes | null {
  const last = safe(() => lastShiftTonnes(state), null);
  if (!last) return null;
  if (!isSevenDays(period) || !period) {
    return {
      name: `Last shift · ${last.window.kind}`,
      actual: last.actual,
      budget: last.budget,
      design: last.design,
      window: `${hm(last.window.start)}–${hm(last.window.end)}`,
    };
  }
  const lastEnd = last.window.end;
  let actual = 0;
  let budget = 0;
  let design = 0;
  let shifts = 0;
  for (const [seriesId, readingId] of [
    ['rs-milled-day', 'rd-kpi-milled-day'],
    ['rs-milled-night', 'rd-kpi-milled-night'],
  ] as const) {
    const points = safe(() => seriesAt(seriesId, state).points, []);
    const shiftDesign = readingByIdLookup(readingId)?.design ?? 0;
    for (const point of points) {
      if (point.value === null) continue;
      if (diffMinutes(point.at, period.window.start) < 0) continue;
      // The point's stamp is its shift's start; the shift has ended once it
      // starts twelve hours or more before the current shift does.
      if (diffMinutes(lastEnd, point.at) < 12 * 60) continue;
      actual += point.value;
      budget += last.budget;
      design += shiftDesign;
      shifts += 1;
    }
  }
  if (shifts === 0) return null;
  return {
    name: `7 days · ${plural(shifts, 'shift')}`,
    actual,
    budget,
    design: design > 0 ? design : undefined,
    window: '',
  };
}

// ---- The circuit read -------------------------------------------------------------------

export type CroLever = {
  key: string;
  /** The instrument or reagent tag, set in mono before the face. */
  tag: string;
  face: string;
  comparison: string;
  off: boolean;
  /** The short name the strip lists when this lever is off setpoint. */
  short: string;
  /** The open case whose subject is this off-setpoint lever (C's code on the chip). */
  caseCode?: { code: string; caseId: string };
  title: string;
};

export type CroStage = {
  id: 'grind' | 'pb-rougher' | 'pb-cleaner' | 'zn-rougher' | 'zn-cleaner';
  label: string;
  grade: string;
  gradeSub: string;
  levers: readonly CroLever[];
};

export type CroCircuitRead = {
  meta: string;
  stages: readonly CroStage[];
  total: number;
  off: readonly CroLever[];
};

const STAGE_GRADE_READING: Record<Exclude<CroStage['id'], 'grind'>, { readingId: string; sub: string }> = {
  'pb-rougher': { readingId: 'rd-pb-rgh-con-pb', sub: 'Pb conc' },
  'pb-cleaner': { readingId: 'rd-kpi-pb-grade', sub: 'Pb conc' },
  'zn-rougher': { readingId: 'rd-zn-rgh-con-zn', sub: 'Zn conc' },
  'zn-cleaner': { readingId: 'rd-kpi-zn-grade', sub: 'Zn conc' },
};

function formatLeverValue(reading: Reading | undefined, value: number): string {
  const decimals = reading?.decimals ?? (reading?.unit === 'pH' || reading?.unit === 'g/t' ? 1 : Number.isInteger(value) ? 0 : 1);
  return value.toFixed(decimals);
}

function formatUnit(unit: string): string {
  if (unit === 'm3/min') return 'm³/min';
  return unit;
}

/** The open case a lever is the subject of: its control-mode check names the
 *  loop, or its rule or cause candidate names the lever's tag as a whole word
 *  (ZN-RGH-PH's rule confirms AIC5002; AG-CONC's `IF 3418A < 45 g/t`). */
function caseForLever(tags: readonly string[], cases: readonly Case[]): CroLever['caseCode'] {
  const names = (row: Case, tag: string) => {
    const escaped = tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^A-Za-z0-9-])${escaped}([^A-Za-z0-9-]|$)`).test(`${row.ifThen ?? ''} ${row.causeCandidate ?? ''}`);
  };
  const hit = cases
    .filter(isOpenCase)
    .find((row) => tags.some((tag) => row.controlModeCheck?.tag === tag || names(row, tag)));
  return hit ? { code: hit.code ?? hit.id, caseId: hit.id } : undefined;
}

function flotationLever(
  chip: LeverChip,
  stageLabel: string,
  state: SessionState,
  window: ShiftWindow | null,
  cases: readonly Case[],
): CroLever {
  const reading = readingByIdLookup(chip.readingId);
  const head = chip.label.split(' · ')[0] ?? chip.label;
  const isPh = chip.role === 'zn-ph-loop';
  // A reagent chip leads with the reagent's own name; an instrument chip with its tag.
  const tag = head.startsWith(`${chip.tag} `) ? chip.tag : (reading?.label ?? chip.tag);
  const face = head.startsWith(`${tag} `) ? head.slice(tag.length + 1) : head;
  const value = windowValue(chip.readingId, state, window) ?? chip.value;
  // The Zn flotation pH reads its one setpoint field (ZN_PH_SETPOINT), not the band midpoint.
  const setpoint = isPh ? znPhSetpointAt(state).value : chip.setpoint;
  const deltaPct = setpoint > 0 ? ((value - setpoint) / setpoint) * 100 : 0;
  const off = Math.abs(deltaPct) > 5;
  const unit = formatUnit(reading?.unit ?? chip.unit);
  const valueText = formatLeverValue(reading, value);
  const faceText = isPh
    ? `lime pH ${valueText}`
    : face.replace(/-?[\d.]+(?=\s|$)/, valueText);
  const spText = reading?.unit === 'pH' ? setpoint.toFixed(1) : String(Math.round(setpoint));
  // The strip names an instrument lever by its circuit and what it moves (`Zn froth`), a reagent by its name.
  const isInstrument = tag === chip.tag && head.startsWith(`${chip.tag} `) && !reading?.unit.includes('g/t');
  const shortName = isPh
    ? 'Zn pH'
    : isInstrument
      ? `${stageLabel.split(' ')[0]} ${face.split(' ')[0]}`
      : tag.replace(/^Aero5100$/, 'Aero 5100');
  return {
    key: chip.readingId,
    tag,
    face: faceText,
    comparison: off ? `SP ${spText}` : 'at SP',
    off,
    short: shortName,
    caseCode: off ? caseForLever([chip.tag, tag], cases) : undefined,
    title: `${chip.tag} · ${reading?.label ?? ''} · ${valueText}${unit && unit !== 'pH' ? ` ${unit}` : ''} vs SP ${spText}`,
  };
}

type GrindLeverSpec = { readingId: string; tag?: string; face: string; short: string; basis: 'design' | 'band' };

const GRIND_LEVERS: readonly GrindLeverSpec[] = [
  { readingId: 'rd-kpv-bm-feed', tag: 'WIC1201A', face: 'feed', short: 'feed', basis: 'design' },
  { readingId: 'rd-kpv-mill-dens', face: 'discharge dens.', short: 'discharge density', basis: 'band' },
  { readingId: 'rd-kpv-cyc-p', face: 'cyclone', short: 'cyclone pressure', basis: 'band' },
  { readingId: 'rd-kpv-regrind-p80', face: 'regrind P80', short: 'regrind P80', basis: 'band' },
];

/** Fresh feed in stph: `rd-kpi-ore-td` ÷ 24 at the cursor, or its window mean. */
function feedStph(state: SessionState, window: ShiftWindow | null): { value: number; design: number } | null {
  const ore = readingAt('rd-kpi-ore-td', state);
  const daily = windowValue('rd-kpi-ore-td', state, window) ?? ore?.value;
  if (daily === undefined || ore?.design === undefined) return null;
  return { value: daily / 24, design: ore.design / 24 };
}

function grindLever(spec: GrindLeverSpec, state: SessionState, window: ShiftWindow | null): CroLever | null {
  const reading = readingByIdLookup(spec.readingId);
  if (!reading) return null;
  const unit = formatUnit(reading.unit);
  const unitSep = unit === '%' ? '' : ' ';
  if (spec.basis === 'design') {
    const feed = feedStph(state, window);
    if (!feed) return null;
    const off = feed.value < feed.design;
    return {
      key: spec.readingId,
      tag: spec.tag ?? reading.tag ?? '',
      face: `${spec.face} ${feed.value.toFixed(1)} ${unit}`,
      comparison: `design ${feed.design.toFixed(1)}`,
      off,
      short: spec.short,
      title: `${spec.tag ?? reading.tag} · ${reading.label} · ${feed.value.toFixed(1)} ${unit} vs ${feed.design.toFixed(1)} ${unit} design`,
    };
  }
  const value = windowValue(spec.readingId, state, window);
  const band = reading.band;
  if (value === undefined || band?.lo === undefined || band.hi === undefined) return null;
  const off = value < band.lo || value > band.hi;
  const valueText = formatLeverValue(reading, value);
  return {
    key: spec.readingId,
    tag: spec.tag ?? '',
    face: `${spec.face} ${valueText}${unitSep}${unit}`,
    comparison: `${band.lo}–${band.hi}`,
    off,
    short: spec.short,
    title: `${reading.tag ?? ''} · ${reading.label} · ${valueText}${unitSep}${unit} vs ${band.lo}–${band.hi}`,
  };
}

function gradeText(readingId: string, state: SessionState, window: ShiftWindow | null, suffix: string): string {
  const reading = readingByIdLookup(readingId);
  const value = windowValue(readingId, state, window);
  if (!reading || value === undefined) return UNMEASURED;
  return `${formatLeverValue(reading, value)}${suffix}`;
}

/** The circuit read (A, with C's case code on the lever chip): five stages in
 *  plant order, each with its grade and its levers; an at-setpoint lever is a
 *  neutral chip and only an off-setpoint lever takes the warning wash. Over
 *  7 days every lever and grade reads its window mean. */
export function croCircuitRead(
  state: SessionState,
  cases: readonly Case[],
  period: RosterPeriod | null,
): CroCircuitRead {
  const window = isSevenDays(period) && period ? period.window : null;
  const p80 = readingByIdLookup('rd-spec-p80');
  const grind: CroStage = {
    id: 'grind',
    label: 'Grinding',
    grade: gradeText('rd-spec-p80', state, window, ` ${p80?.unit ?? 'µm'}`),
    gradeSub: p80?.design !== undefined ? `P80 · vs ${p80.design} design` : 'P80',
    levers: GRIND_LEVERS.map((spec) => safe(() => grindLever(spec, state, window), null)).filter(
      (row): row is CroLever => row !== null,
    ),
  };
  const flotation: CroStage[] = safe(() => croStageRead(state), []).map((row) => ({
    id: row.stageId,
    label: row.label,
    grade: gradeText(STAGE_GRADE_READING[row.stageId].readingId, state, window, '%'),
    gradeSub: STAGE_GRADE_READING[row.stageId].sub,
    levers: row.levers.map((chip) => flotationLever(chip, row.label, state, window, cases)),
  }));
  const stages = [grind, ...flotation];
  const all = stages.flatMap((stage) => stage.levers);
  return {
    meta: window ? '7-day mean' : `at ${hm(state.cursor)}`,
    stages,
    total: all.length,
    off: all.filter((lever) => lever.off),
  };
}

/** The guardrail under the circuit read on its own view (`Aero 5100 11 %
 *  under design · 4 days · …`), in `pt`. */
export function croGuardrailLine(state: SessionState): string {
  const line = safe(() => recoveryForecastRead(state, { compactCharts: true }).guardrailLine ?? '', '');
  return line.replace(/\bpts\b/g, 'pt');
}

export type CroThroughputGap = {
  actual: string;
  lead: string;
  rest: string;
  causes: readonly { caseId: string; title: string; code: string; tdEst: number }[];
  owners: readonly string[];
};

/** Throughput over 24 h against design, the gap attributed to the open
 *  grinding cases (the landing's existing `[syn] est.` split). */
export function croThroughputGap(state: SessionState, input: PerformanceInput): CroThroughputGap | null {
  const ore = readingAt('rd-kpi-ore-td', state);
  if (ore?.value === undefined || ore.design === undefined) return null;
  const model = safe(() => performanceForRole('cro', input, state, { showMoney: false }), null);
  const causes = (model?.grindingShortfall ?? []).map((row) => {
    const caseItem = input.cases.find((c) => c.id === row.caseId);
    return {
      caseId: row.caseId,
      title: row.title.split(' — ')[0] ?? row.title,
      code: caseItem?.code ?? row.caseId,
      tdEst: row.tdEst,
    };
  });
  const owners = [
    ...new Set(
      causes
        .map((row) => input.cases.find((c) => c.id === row.caseId))
        .map((c) => (c?.handler ? `${c.handler.name} · ${roleShortLabel(c.handler.role)}` : c ? roleShortLabel(c.assignedTo) : ''))
        .filter(Boolean),
    ),
  ];
  const gap = ore.design - ore.value;
  return {
    actual: `${fmtT(ore.value)} t/d · 24 h`,
    lead: gap >= 0 ? `${fmtT(gap)} t/d short of ${fmtT(ore.design)} design` : `${fmtT(-gap)} t/d over ${fmtT(ore.design)} design`,
    rest: ore.target !== undefined ? ` · Budget ${fmtT(ore.target)}` : '',
    causes,
    owners,
  };
}

// ---- The status band --------------------------------------------------------------------

export type CroBandCell = {
  id: 'zn' | 'pb' | 'ag' | 'feed';
  label: string;
  tone: VizTone;
  value: string;
  unit: string;
  captionLead: string;
  caption: string;
  sub: string;
  spark?: { points: readonly number[]; design: number };
};

const METALS = [
  { id: 'zn', metal: 'Zn', readingId: 'rd-kpi-zn-rec' },
  { id: 'pb', metal: 'Pb', readingId: 'rd-kpi-pb-rec' },
  { id: 'ag', metal: 'Ag', readingId: 'rd-kpi-ag-rec' },
] as const;

/** One gap rule for every recovery, the Control metallurgist's (P26 r1):
 *  more than 2 pt below design is bad, more than 0.5 pt warn. */
function recoveryTone(actual: number, design: number): VizTone {
  const gap = design - actual;
  if (gap > 2) return 'bad';
  if (gap > 0.5) return 'warn';
  return 'ok';
}

/** The band (adjudication 5 folds the meter bars and the recovery trend into
 *  it): each recovery's last assay against design first, Budget second, its
 *  7-day change and trend over the design line; then BM01 fresh feed now. */
export function croStatusBand(state: SessionState): CroBandCell[] {
  const forecasts = safe(() => recoveryForecastRead(state, { compactCharts: true }).forecasts, []);
  const cells: CroBandCell[] = METALS.map(({ id, metal, readingId }) => {
    const reading = readingAt(readingId, state);
    const forecast = forecasts.find((row) => row.metal === metal);
    const actual = forecast?.current ?? reading?.value;
    const design = forecast?.design ?? reading?.design;
    const label = `${metal} recovery · last assay`;
    if (actual === undefined || design === undefined) {
      return { id, label, tone: 'neutral', value: UNMEASURED, unit: '', captionLead: '', caption: '', sub: '' };
    }
    const points = (forecast?.points ?? []).map((point) => point.value);
    const week = points.length > 1 ? points[points.length - 1]! - points[0]! : undefined;
    return {
      id,
      label,
      tone: recoveryTone(actual, design),
      value: actual.toFixed(1),
      unit: '%',
      captionLead: formatPtDelta(recoveryPtDelta(actual, design)),
      caption: ` vs${NBSP}${design.toFixed(1)}% design${reading?.target !== undefined ? ` · Budget${NBSP}${reading.target.toFixed(1)}%` : ''}`,
      sub: week !== undefined ? `${formatPtDelta(week)} over 7 d` : '',
      spark: points.length > 1 ? { points, design } : undefined,
    };
  });
  const feed = feedStph(state, null);
  const feedReading = readingByIdLookup('rd-kpv-bm-feed');
  const band = feedReading?.band;
  cells.push(
    feed
      ? {
          id: 'feed',
          label: 'BM01 fresh feed · now',
          tone: feed.value < feed.design ? 'warn' : 'ok',
          value: feed.value.toFixed(1),
          unit: 'stph',
          captionLead: `vs ${feed.design.toFixed(1)} stph design`,
          caption: feedReading?.tag ? ` · ${feedReading.tag}` : '',
          sub: band?.lo !== undefined && band.hi !== undefined ? `normal band ${band.lo}–${band.hi} stph` : '',
        }
      : { id: 'feed', label: 'BM01 fresh feed · now', tone: 'neutral', value: UNMEASURED, unit: '', captionLead: '', caption: '', sub: '' },
  );
  return cells;
}

// ---- The loops and the variables --------------------------------------------------------

export type CroLoop = {
  tag: string;
  name: string;
  mode: 'manual' | 'local' | 'auto' | 'cascade';
  modeLabel: string;
  tone: 'warn' | 'info' | 'neutral';
  since: string;
};

const MODE_LABEL: Record<string, string> = { manual: 'Manual', local: 'Local', auto: 'Auto', cascade: 'Cascade' };

/** The control loops with their mode and time in mode; Manual is the one
 *  mode that takes the warning tone, Local the info tone, Auto stays quiet. */
export function croLoops(): CroLoop[] {
  return CONTROL_LOOPS.map((loop) => ({
    tag: loop.tag,
    // The loop name drops the instrument aliases the seed carries in brackets (adjudication 8).
    name: loop.name.replace(/\s*\([^)]*\)\s*$/, ''),
    mode: loop.mode as CroLoop['mode'],
    modeLabel: `${MODE_LABEL[loop.mode] ?? loop.mode} · ${bindUnit(loop.duration)}`,
    tone: loop.mode === 'manual' ? 'warn' : loop.mode === 'local' ? 'info' : 'neutral',
    since: loop.modeChangedAt ? `since ${loop.modeChangedAt}` : '',
  }));
}

export type CroVariableStatus = 'ok' | 'off' | 'low' | 'high';

export type CroVariableRow = {
  id: string;
  label: string;
  reading: string;
  setpoint: string;
  status: CroVariableStatus;
};

export type CroVariableGroup = { id: string; label: string; rows: readonly CroVariableRow[] };

const VARIABLE_GROUPS: readonly { id: string; label: string; rowIds: readonly string[] }[] = [
  { id: 'grind', label: 'Grinding', rowIds: ['cro-r1', 'cro-r2', 'cro-r3', 'cro-r4'] },
  { id: 'pb', label: 'Pb flotation', rowIds: ['cro-r5', 'cro-r8', 'cro-r9'] },
  { id: 'zn', label: 'Zn flotation', rowIds: ['cro-r6', 'cro-r7', 'cro-r10'] },
  { id: 'conc', label: 'Concentrates', rowIds: ['cro-r11', 'cro-r12'] },
];

const STATUS_BY_CELL: Record<string, CroVariableStatus> = { OK: 'ok', WARN: 'off', LOW: 'low', HIGH: 'high' };

function variableRow(row: RoleTable['rows'][number], state: SessionState): CroVariableRow {
  const [label = '', reading = '', setpoint = '', status = 'OK'] = row.cells;
  if (row.id === 'cro-r1') {
    // Fresh feed: `rd-kpi-ore-td` ÷ 24 against its design, as every feed figure reads.
    const feed = feedStph(state, null);
    if (feed) {
      return {
        id: row.id,
        label,
        reading: `${feed.value.toFixed(1)} stph`,
        setpoint: `Design ${feed.design.toFixed(1)}`,
        status: feed.value < feed.design ? 'off' : 'ok',
      };
    }
  }
  if (row.id === 'cro-r6') {
    // Zn float pH: AT-150 at the cursor, the one setpoint field and the Control Plan band (adjudication 6).
    const value = valueAt('rd-kpv-zn-ph', state);
    const band = CONTROL_PLAN_ZN_ROWS.find((cp) => cp.id === 'cp-zn-ph')?.specOverlay;
    if (value !== undefined && band?.lo !== undefined && band.hi !== undefined) {
      return {
        id: row.id,
        label,
        reading: value.toFixed(1),
        setpoint: `SP ${znPhSetpointAt(state).value.toFixed(1)} · band ${band.lo.toFixed(1)}–${band.hi.toFixed(1)}`,
        status: value < band.lo ? 'low' : value > band.hi ? 'high' : 'ok',
      };
    }
  }
  return { id: row.id, label, reading, setpoint, status: STATUS_BY_CELL[status] ?? 'ok' };
}

/** The twelve key process variables grouped by circuit; the concentrate
 *  grades read their `ConcentrateSpec` verdicts (`croKpvTable`, #730). */
export function croVariables(table: RoleTable | undefined, state: SessionState): {
  groups: CroVariableGroup[];
  total: number;
  offBand: number;
} {
  if (!table) return { groups: [], total: 0, offBand: 0 };
  const derived = safe(() => croKpvTable(table, state), table);
  const rows = derived.rows.map((row) => variableRow(row, state));
  const byId = new Map(rows.map((row) => [row.id, row]));
  const grouped = new Set<string>();
  const groups = VARIABLE_GROUPS.map((group) => {
    const members = group.rowIds.map((id) => byId.get(id)).filter((row): row is CroVariableRow => Boolean(row));
    members.forEach((row) => grouped.add(row.id));
    return { id: group.id, label: group.label, rows: members };
  }).filter((group) => group.rows.length > 0);
  const rest = rows.filter((row) => !grouped.has(row.id));
  if (rest.length > 0) groups.push({ id: 'other', label: 'Other', rows: rest });
  return { groups, total: rows.length, offBand: rows.filter((row) => row.status !== 'ok').length };
}

// ---- Awaiting my action -----------------------------------------------------------------

export type CroAction = {
  caseId: string;
  code: string;
  title: string;
  severity: { label: string; tone: VizTone };
  step: string;
  who?: string;
  context: string;
  gate?: CroGate;
};

export type CroGate = {
  evidenceId: string;
  step: string;
  by: string;
  /** Set once the step is recorded: `Done · 11:30 · D. Okafor · CRO`. */
  recorded?: string;
};

const SEVERITY_RANK: Record<Case['severity'], number> = { high: 0, med: 1, low: 2, monitor: 3 };

function capitalise(text: string): string {
  return text.length > 0 ? `${text[0]!.toUpperCase()}${text.slice(1)}` : text;
}

function firstClause(text: string): string {
  return (text.split(/;\s|\.\s|\.$/)[0] ?? text).trim();
}

const STEP_ARTICLE = /^(the|a|an)$/i;

/** `CuSO₄ titration` from the step `Arrange CuSO₄ solution-strength titration`:
 *  the leading verb drops with any qualifier that is not the head noun, so the
 *  onward card names the step from the same source the action step reads
 *  (item 4), never from a typed short name. */
function stepShortName(step: string): string {
  const words = step.replace(/[.;,:]+$/, '').split(/\s+/).filter(Boolean);
  const head = words.at(-1) ?? '';
  const core = words.slice(1).filter((word) => !STEP_ARTICLE.test(word));
  const lead = core.find((word) => !word.includes('-')) ?? core[0] ?? '';
  return lead && lead !== head ? `${lead} ${head}` : head;
}

/** The step the control room owes a case: the escalation ladder's current
 *  instruction, else the case's own seeded next step, else its open evidence
 *  request, else the diagnosis the case was routed on with its state. The last
 *  fallback never repeats the title: a diagnosis clause the title already
 *  states (AIR-VALVE's cause was its title) drops to the state alone (item 2). */
function stepFor(caseItem: Case): string {
  if (caseItem.ladder) {
    const read = ladderRead(caseItem, caseItem.ladder.enteredAt);
    if (read) return capitalise(read.currentTier.instruction);
  }
  if (caseItem.nextStep) return capitalise(caseItem.nextStep);
  const ask = caseItem.evidenceRequests.find(
    (row) => row.status === 'open' && (row.target === 'cro' || row.target === 'operator'),
  );
  if (ask) return capitalise(firstClause(ask.text));
  const cause = firstClause((caseItem.causeCandidate ?? '').split(' — ')[0] ?? '');
  const state = caseItem.state.replace(/-/g, ' ');
  const branch = caseItem.branch === 'zero-touch' ? ', zero-touch' : '';
  const title = caseItem.title.toLowerCase();
  const repeatsTitle = (text: string) => title.includes(text.toLowerCase());
  if (cause && !repeatsTitle(cause)) return `${capitalise(cause)} · ${state}${branch}`;
  return `${capitalise(state)}${branch}`;
}

/** `Titration arranged` from `arrange … titration`; the recorded step's value. */
function gateStepValue(instruction: string): string {
  const match = instruction.match(/^arrange\s+(?:the\s+)?(.+)$/i);
  if (!match) return 'Done';
  const noun = match[1]!.split(/\s+/).at(-1) ?? 'step';
  return `${capitalise(noun)} arranged`;
}

/** The cases Advisory counts as awaiting the control room, in the order they
 *  need doing: the soonest escalation clock first, then severity, then the
 *  oldest raise. A case holds a control-room step when it is open and either
 *  (a) it is assigned to the CRO or the operator and its state is not
 *  `outcome-documented` (LIC4010 is the documented operator-held loop, no step
 *  left), or (b) the operator or the CRO holds its open ask — TRAMP-CV001 is
 *  Maintenance's case, but M. Reid (operator) holds `ev-cv001-magnet`, so its
 *  walk-out is the control room's to run (item 1). */
export function croAwaiting(cases: readonly Case[], state: SessionState): Case[] {
  const clock = (row: Case) => {
    const read = safe(() => ladderRead(row, state.cursor), null);
    return read ? read.remainingMinutes : Number.POSITIVE_INFINITY;
  };
  const heldByControlRoom = (row: Case) => row.handler?.role === 'cro' || row.handler?.role === 'operator';
  return cases
    .filter(
      (row) =>
        isOpenCase(row) &&
        (((row.assignedTo === 'cro' || row.assignedTo === 'operator') && row.state !== 'outcome-documented') ||
          (heldByControlRoom(row) &&
            row.evidenceRequests.some(
              (ask) => ask.status === 'open' && (ask.target === 'cro' || ask.target === 'operator'),
            ))),
    )
    .slice()
    .sort(
      (a, b) =>
        clock(a) - clock(b) ||
        SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
        a.raisedAt.localeCompare(b.raisedAt),
    );
}

/** `Your actions — in order` (B's context line under each action): the step
 *  in ink, who owns it, the triggering reading and the escalation clock. The
 *  first case's own open evidence step carries the Record gate: one record,
 *  shown here and in the case drawer (adjudication 4). */
export function croActions(state: SessionState, advisory: AdvisorySessionState): CroAction[] {
  const cases = safe(() => casesWithSession(state, advisory), []);
  return croAwaiting(cases, state).map((caseItem, index) => {
    const ladder = safe(() => ladderRead(caseItem, state.cursor), null);
    const ladderClock = ladder ? formatLadderLine(ladder).clock : null;
    // A plain respond-by past its deadline reads the ladder formatter's own
    // overdue state (`overdue by 10m`), never a typed one (item 1).
    const clock = ladderClock ?? safe(() => respondByOverdueClock(caseItem, state.cursor), null);
    const reading = firstClause(caseItem.observation ?? '');
    const context = [reading, clock ? bindClock(clock) : `raised ${formatCaseClock(caseItem.raisedAt, state.cursor)}`]
      .filter(Boolean)
      .join(' · ');
    const who = caseItem.handler ? `${caseItem.handler.name} · ${roleShortLabel(caseItem.handler.role)}` : undefined;
    let gate: CroGate | undefined;
    if (index === 0 && caseItem.handler) {
      const ask = caseItem.evidenceRequests.find((row) => row.target === 'cro' || row.target === 'operator');
      if (ask) {
        const answered = ask.status === 'answered';
        const at = ask.answeredAt && ask.answeredAt !== 'now' ? ask.answeredAt : hm(state.cursor);
        gate = {
          evidenceId: ask.id,
          step: answered && ask.answer ? ask.answer : gateStepValue(ladder?.currentTier.instruction ?? ask.text),
          by: caseItem.handler.name,
          recorded: answered ? `Done · ${at} · ${who}` : undefined,
        };
      }
    }
    return {
      caseId: caseItem.id,
      code: caseItem.code ?? caseItem.id,
      title: caseItem.title,
      severity: severityChip(caseItem),
      step: stepFor(caseItem),
      who,
      context,
      gate,
    };
  });
}

export type CroOnwardCard = {
  count: number;
  lead: string;
  /** The first case's step, named short (not its code), and its escalation clock, where one runs. */
  first?: { name: string; clock: string | null };
  loopLine?: string;
};

/** The onward card: Advisory's `Awaiting my action` count, the first step's
 *  case and clock, and the loops held in Manual past 2 h (adjudication 3). The
 *  first is named by its step — `CuSO₄ titration` — as the comp draws it,
 *  derived from the same step the action row prints (item 4). */
export function croOnwardCard(state: SessionState, advisory: AdvisorySessionState): CroOnwardCard {
  const cases = safe(() => casesWithSession(state, advisory), []);
  const awaiting = croAwaiting(cases, state);
  const first = awaiting[0];
  const ladder = first ? safe(() => ladderRead(first, state.cursor), null) : null;
  const clock = ladder ? formatLadderLine(ladder).clock : null;
  const manual = loopsManualPastHours(CONTROL_LOOPS, 2);
  return {
    count: awaiting.length,
    lead: 'assigned to CRO / operator',
    first: first
      ? { name: stepShortName(stepFor(first)) || first.code || first.id, clock: clock ? bindClock(clock) : null }
      : undefined,
    loopLine:
      manual.length > 0
        ? `${plural(manual.length, 'loop')} in manual >${NBSP}2${NBSP}h · ${manual.map((loop) => `${loop.tag} · ${bindUnit(loop.duration)}`).join(' · ')}`
        : undefined,
  };
}

// ---- The Circuit read view's right rail -------------------------------------------------

export type CroInsight = {
  code: string;
  caseId: string;
  body: string;
  pill?: { label: string; rest: string };
};

/** Why Zn recovery is below design: the ZN-REC observation without the
 *  recovery sentence the band already states (say it once). */
export function croZnInsight(state: SessionState, input: PerformanceInput): CroInsight | null {
  const model = safe(() => performanceForRole('cro', input, state, { showMoney: false }), null);
  const panel = model?.aiBand?.panels.find((row) => row.id === 'cro-zn-cause');
  const observation = panel?.rows?.[0];
  if (!panel || !observation) return null;
  const body = String(observation.content ?? '')
    .split(/(?<=\.)\s+/)
    .filter((sentence) => !/^Recovery\b/.test(sentence))
    .join(' ');
  const pillText = observation.pillText ?? '';
  const split = pillText.indexOf(' · ');
  return {
    code: observation.provenance ?? panel.provenance ?? 'ZN-REC',
    caseId: 'case-zn-recovery',
    body,
    pill: pillText ? { label: split < 0 ? pillText : pillText.slice(0, split), rest: split < 0 ? '' : pillText.slice(split + 3) } : undefined,
  };
}

/** Worth checking or sampling this shift: the ZN-REC case's open asks of the
 *  operator or CRO, and its steps proposed for today. */
export function croChecks(state: SessionState, input: PerformanceInput): { text: string; tail: string }[] {
  const model = safe(() => performanceForRole('cro', input, state, { showMoney: false }), null);
  const panel = model?.aiBand?.panels.find((row) => row.id === 'cro-zn-cause');
  return (panel?.checklist ?? []).map((row) => ({ text: capitalise(row.text), tail: row.tail ?? '' }));
}

/** Payable metal levers: payable metal per +1 pt of recovery, never money. */
export function croPayableLevers(state: SessionState): { metal: string; value: string }[] {
  const order = ['Zn', 'Pb', 'Ag'];
  return safe(() => recoveryPayableLevers(state), [])
    .slice()
    .sort((a, b) => order.indexOf(a.metal) - order.indexOf(b.metal))
    .map((row) => ({ metal: row.metal, value: `${row.leverPerPt.toFixed(1)} ${row.unit} payable` }));
}

// ---- Since last shift -------------------------------------------------------------------

export type CroBriefLine = {
  at: string;
  title: string;
  verb: string;
  flag: string;
  caseId: string;
  outcome?: string;
};

export type CroSince = {
  title: string;
  meta: string;
  lines: readonly CroBriefLine[];
  countLine?: string;
  counts: { raised: number; closed: number; decisions: number };
};

/** The shift brief over the period (its lines open the case drawer). */
export function croSince(state: SessionState, input: PerformanceInput, period: RosterPeriod | null): CroSince {
  const window = period?.window;
  const title = !period || period.id !== 'seven-days' ? 'Last shift' : period.label;
  const brief = safe(
    () => (window ? shiftBriefRead(state, input.cases, input.actions, ROLE, window, title) : shiftBriefRead(state, input.cases, input.actions, ROLE)),
    null,
  );
  const counts = window
    ? safe(() => shiftBriefEventCount(state, input.cases, input.actions, ROLE, window), { raised: 0, closed: 0, decisions: 0, total: 0 })
    : { raised: 0, closed: 0, decisions: 0, total: 0 };
  const start = window?.start;
  const meta = start ? `${start.slice(8, 10)} ${monthOf(start)} ${hm(start)} → now` : '';
  return {
    title: brief?.title ?? title,
    meta,
    lines: (brief?.lines ?? []).map((line) => ({
      at: line.at,
      title: line.title,
      verb: line.verb,
      flag: line.flagTail,
      caseId: line.caseId,
      outcome: line.outcome,
    })),
    countLine: sinceCountLine(brief?.countLine),
    counts,
  };
}

/** The brief's foot names only what the strip's since cell does not: how many
 *  of the window's events the list shows (`5 of 36 shown`). The raised, closed
 *  and decision counts are the cell's, so they are not restated here (item 3). */
function sinceCountLine(countLine?: string): string | undefined {
  const head = countLine?.replace(/^showing /, '').split(' · ')[0]?.trim();
  return head && /^\d+ of \d+$/.test(head) ? `${head} shown` : undefined;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function monthOf(stamp: string): string {
  return MONTHS[Number(stamp.slice(5, 7)) - 1] ?? '';
}

export type CroAlarmRow = {
  id: string;
  code: string;
  priority: string;
  state: 'active' | 'acked';
  raised: string;
  text: string;
  /** The DCS tag inside `text`, kept on one line where it renders. */
  tag?: string;
};

/** The DCS alarms raised in the period, P4 folded into one line as the
 *  Advisory panel folds them (the comp's `1 P4 hidden · PUMP-04`), active
 *  before acked. An alarm on the Zn pH loop that quotes its setpoint keeps its
 *  raise-time text and names the setpoint now (the one setpoint timeline,
 *  adjudication 6). `active` counts every DCS alarm still active in the
 *  window, the folded P4 included — the fold hides the row from the list, not
 *  the alarm from the count (item 8). */
export function croAlarms(
  state: SessionState,
  advisory: AdvisorySessionState,
  period: RosterPeriod | null,
): { rows: CroAlarmRow[]; hiddenP4: number; hiddenP4Codes: string[]; active: number } {
  const all = safe(() => alarmsWithSession(advisory, state), [] as Alarm[]);
  const inWindow = (alarm: Alarm) => !period || safe(() => windowContains(alarm.raisedAt, state.cursor, period.window), true);
  const dcs = dcsAlarmsForPanel(all).visible.filter(inWindow);
  const hidden = dcs.filter((alarm) => alarm.priority === 'P4');
  const shown = dcsAlarmsForPanel(dcs, { hideP4: true }).visible;
  const sp = znPhSetpointAt(state);
  const ordered = [...shown.filter((alarm) => alarm.state === 'active'), ...shown.filter((alarm) => alarm.state !== 'active')];
  const rows = ordered.map((alarm) => {
    const raised = formatCaseClock(alarm.raisedAt, state.cursor);
    const message = alarm.message ?? '';
    const onPhLoop = alarm.tag === 'AIC5002' && /SP=/.test(message);
    const text = onPhLoop
      ? `at ${raised}: ${message} · ${alarm.tag} · SP${NBSP}now${NBSP}${sp.value.toFixed(1)}`
      : `${message}${alarm.tag ? ` · ${alarm.tag}` : ''}`;
    return {
      id: alarm.id,
      code: alarm.code,
      priority: alarm.priority,
      state: alarm.state === 'active' ? ('active' as const) : ('acked' as const),
      raised,
      text,
      tag: alarm.tag,
    };
  });
  return {
    rows,
    hiddenP4: hidden.length,
    hiddenP4Codes: hidden.map((alarm) => alarm.code),
    active: dcs.filter((alarm) => alarm.state === 'active').length,
  };
}

// ---- The strip ----------------------------------------------------------------------------

export function croStripCells(
  state: SessionState,
  advisory: AdvisorySessionState,
  input: PerformanceInput,
  table: RoleTable | undefined,
  period: RosterPeriod | null,
): LandingStripCell<CroTabId>[] {
  const tonnes = croTonnes(state, period);
  const circuit = croCircuitRead(state, input.cases, period);
  const loops = croLoops();
  const notAuto = loops.filter((loop) => loop.mode !== 'auto' && loop.mode !== 'cascade');
  const worst = loops.find((loop) => loop.mode === 'manual') ?? notAuto[0];
  const variables = croVariables(table, state);
  const since = croSince(state, input, period);
  const alarms = croAlarms(state, advisory, period);
  const gap = tonnes ? tonnes.actual - tonnes.budget : 0;
  return [
    {
      id: 'shift',
      name: tonnes?.name ?? 'Last shift',
      figure: tonnes ? fmtT(tonnes.actual) : UNMEASURED,
      figureContext: tonnes ? 't' : undefined,
      comparisonLead: tonnes ? `vs budget ${fmtT(tonnes.budget)} t · ${signed(gap, 't')}` : '',
      comparisonLeadTone: tonnes ? (gap >= 0 ? 'ok' : 'warn') : undefined,
      comparisonRest: tonnes
        ? `${tonnes.design !== undefined ? ` · design${NBSP}${fmtT(tonnes.design)}${NBSP}t` : ''}${tonnes.window ? ` · ${tonnes.window}` : ''}`
        : '',
      actor: 'System',
      actorLabel: 'Historian',
    },
    {
      id: 'circuit',
      name: 'Circuit read',
      figure: String(circuit.off.length),
      figureContext: 'off setpoint',
      comparisonLead: `of ${circuit.total} levers`,
      comparisonRest: circuit.off.length > 0 ? ` · ${circuit.off.map((lever) => lever.short).join(' · ')}` : '',
      actor: 'Engine',
    },
    {
      id: 'loops',
      name: 'Loops & variables',
      figure: String(notAuto.length),
      figureContext: 'not in Auto',
      comparisonLead: worst ? `${worst.tag} ${worst.modeLabel}` : '',
      comparisonRest: `${worst ? ' · ' : ''}${variables.offBand} of ${variables.total} variables off band`,
      actor: 'System',
      actorLabel: 'DCS',
    },
    {
      id: 'since',
      name: isSevenDays(period) && period ? capitalise(windowSinceLabel(period.window)) : 'Since last shift',
      figure: String(since.counts.raised),
      figureContext: 'raised',
      comparisonLead: `${since.counts.closed} closed`,
      comparisonRest: ` · ${plural(since.counts.decisions, 'decision')} · ${plural(alarms.active, 'DCS alarm')} active`,
      actor: 'Engine',
    },
  ];
}
